import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

/**
 * The photo viewer: one image, its label, and an arrow either side.
 *
 * **The lightbox and nothing else.** The portal's `ImageCarousel` is a whole section — a
 * thumbnail gallery, a category filter, and a lightbox *inside* it — so wrapping it in a
 * dialog meant a dealer opened two things to look at one photo. This is the same
 * `react-image-lightbox` that section opens, mounted directly, with the same title,
 * caption, wrap-around navigation, and modal z-index.
 *
 * ## Why this waits before it opens
 *
 * `react-image-lightbox` starts loading its images on mount, and when a load finishes it
 * **discards the result if the matching `src` prop has changed in the meantime** — with no
 * retry, and no reload until the sources change again. That is a first image that spins
 * forever and then appears the instant you navigate away and back.
 *
 * The portal's own usage never trips this, because its gallery has already downloaded those
 * images as thumbnails: the lightbox's load resolves from browser cache before any prop can
 * move. Opening straight from a card has no such warm-up, so two things stand in for it.
 *
 * 1. **The first image is preloaded before the lightbox mounts**, giving it the same warm
 *    cache the gallery would have.
 * 2. **The image list is frozen for one viewing.** The query refreshes in the background,
 *    and a fresh result must not swap a `src` out from under a load in flight. The next
 *    opening picks up whatever landed.
 */
export interface ScenarioPhotoLightboxImage {
  /** The full-size source. */
  original?: string | null;
  /** The human-readable capture name — the lightbox's title. */
  label?: string | null;
  /** Longer context; shown only when it says something the label does not. */
  description?: string | null;
}

export interface ScenarioPhotoLightboxProps {
  /**
   * Identifies what is being viewed — the leg and phase. `null` closes it.
   *
   * A key rather than a boolean, because it is also what scopes the frozen set: choosing a
   * different set is a new session, and the old one's frozen images fall away with it.
   */
  sessionKey: string | null;
  images: ScenarioPhotoLightboxImage[];
  onClose: () => void;
}

interface ScenarioPhotoSession {
  key: string;
  images: ScenarioPhotoLightboxImage[];
}

const ScenarioPhotoLightbox: React.FC<ScenarioPhotoLightboxProps> = ({
  sessionKey,
  images,
  onClose,
}) => {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [session, setSession] = useState<ScenarioPhotoSession | null>(null);

  const firstSrc = images[0]?.original ?? null;
  const isCurrent = !!sessionKey && session?.key === sessionKey;

  useEffect(() => {
    if (!sessionKey || !firstSrc || isCurrent) return undefined;

    let cancelled = false;
    const warm = new Image();
    // An error settles too: the lightbox renders its own failure state, which is a better
    // answer than a viewer that never opens at all.
    const settle = () => {
      if (!cancelled) setSession({ key: sessionKey, images });
    };
    warm.onload = settle;
    warm.onerror = settle;
    warm.src = firstSrc;

    return () => {
      cancelled = true;
    };
  }, [sessionKey, firstSrc, isCurrent, images]);

  // The frozen set, and only while it still belongs to the session on screen — so a new
  // selection blanks the viewer rather than showing the previous one's photos.
  const shown = isCurrent ? session.images : [];
  if (!shown.length) return null;

  // Wrap around in both directions, so an arrow is never a dead end.
  const at = (offset: number) => shown[(index + shown.length + offset) % shown.length];
  const current = at(0);

  const handleClose = () => {
    setIndex(0);
    setSession(null);
    onClose();
  };

  return (
    <Lightbox
      imageTitle={current?.label ?? undefined}
      // A description that merely repeats the label is noise under it.
      imageCaption={
        current?.description && current.description !== current.label ? current.description : undefined
      }
      mainSrc={current?.original ?? ''}
      prevSrc={shown.length > 1 ? at(-1)?.original ?? undefined : undefined}
      nextSrc={shown.length > 1 ? at(1)?.original ?? undefined : undefined}
      onCloseRequest={handleClose}
      onMovePrevRequest={() => setIndex((index + shown.length - 1) % shown.length)}
      onMoveNextRequest={() => setIndex((index + 1) % shown.length)}
      reactModalStyle={{
        overlay: { zIndex: theme?.zIndex?.modal },
        content: { zIndex: theme?.zIndex?.modal },
      }}
    />
  );
};

export default ScenarioPhotoLightbox;
